import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Intercom, { boot, update, shutdown, hide, show } from '@intercom/messenger-js-sdk';
import { environment } from '../../environments/environment';
import { AuthData, CurrentUser, IntercomAccessResponse, IntercomCompanyData } from '@app/models';
import { CommonService } from './common.service';
import { LocalStorageService } from './local-storage.service';
import { OrganizationList } from '@app/models/organization.model';

@Injectable({
  providedIn: 'root'
})
export class IntercomService {

  private bootCompleted = false; 

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object, 
    private readonly _localStorageService: LocalStorageService,
    private readonly _commonService: CommonService
  ) {}

  initVisitor() {
    if (isPlatformBrowser(this.platformId)) {
      Intercom({
        app_id: environment.intercomAppId
      });
    }
  }

  initializeUserSession() {
    const authData = this._localStorageService.getAuthData();
    const appProfileData = this._commonService.appProfileDataAsValue;
    const user = appProfileData.user;
    
    if (!appProfileData.organizations?.length) {
      console.warn('Intercom: No organizations found for user, skipping login.');
      return;
    }

    const defaultOrg = appProfileData.organizations.find(org => (org.id == user.user.default_org)) ?? appProfileData.organizations[0];
    const companies: [IntercomCompanyData] = [{
      company_id: `${defaultOrg.id}`,
      name: defaultOrg.name
    }];

    if (!user?.profile?.userEmail || !authData) {
      console.warn('Intercom: Invalid user session, skipping login.');
      return;
    }

    if (authData?.intercomToken) {
      this.login(user, authData.intercomToken, companies);
    } else {
      this.fetchTokenAndLogin(user, companies, authData);
    }
  }

  private fetchTokenAndLogin(user: CurrentUser, companies: [IntercomCompanyData], currentAuthData: AuthData) {
    this._commonService.getIntercomToken(user.profile.userEmail).subscribe({
      next: (response: IntercomAccessResponse) => {
        
        // Save new token to Local Storage
        const updatedAuthData = { ...currentAuthData, intercomToken: response.token };
        this._localStorageService.setAuthData(updatedAuthData);

        // Login with the new token
        this.login(user, response.token, companies);
      },
      error: (err) => {
        console.error('Intercom: Failed to fetch token', err);
      }
    });
  }

  login(user: CurrentUser, token: string, companies: [IntercomCompanyData]) {
    if (isPlatformBrowser(this.platformId)) {
      this.initVisitor();
      boot({
        app_id: environment.intercomAppId,
        user_id: user.profile.userEmail,
        name: `${user.profile.firstName} ${user.profile.lastName}`,
        email: user.profile.userEmail,
        intercom_user_jwt: token, 
        companies: companies, 
        created_at: Math.floor(Date.now() / 1000) // Ensure Unix timestamp
      });
      this.bootCompleted = true;
    }
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      shutdown();
      this.bootCompleted = false;
      hide();
    }
  }

  updateData() {
    if (isPlatformBrowser(this.platformId) && this.bootCompleted) {
      update({});
    }
  }
}